import {observer} from 'mobx-react-lite'
import * as React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  GenericStickerState,
  Hub,
  systemStickers,
  Tag
} from 'yougile-model'
import {RequiredStickerDefaultState} from '@components/add-task/types'
import {Checkbox} from '@components/common/base/checkbox'
import {LinkButtonAdd} from '@components/right-panel/checklists-tab/link-button-add'
import {PinStickersDropdown} from '@components/sticker/pin-sticker-menu'
import {StickerItem} from '@components/sticker/sticker-item'
import {noPropagate} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {useMovementStore} from './movement-store'
import {TwAction} from './remove-assigned-actions'
import {SelectedPinStickers} from './types'

export const TwStickerPanel = tw.div`
  flex
  items-center
  gap-8
  flex-wrap
`

interface Props {
  onChange?: (
    values: RequiredStickerDefaultState,
    stickerId: DataObjectId
  ) => void
  board?: Hub
  values: SelectedPinStickers
}

const excludeStickers = [
  systemStickers.User,
  systemStickers.Repeat,
  systemStickers.Timer,
  systemStickers.Timetracking,
  systemStickers.Stopwatch
]

export const PinStickersActions = observer((props: Props) => {
  const {board, values = {}} = props
  const [state, store] = useMovementStore()
  const lang = Lang.msg.movementTemplates

  const selectedIds = Object.keys(values)
  const stickersInBoard = Sticker.list(board).filter(s => !s.deleted)
  const selectedStickers = stickersInBoard.filter(s =>
    selectedIds.includes(s.id)
  )

  const stickersToSelect = stickersInBoard.filter(
    s =>
      !selectedIds.includes(s.id) &&
      !excludeStickers.includes(s.id) &&
      s.title !== 'Stopwatch'
  )

  const onChange = (sticker: Tag, value: GenericStickerState | undefined) => {
    if (value) {
      store.pinSticker(sticker.id, value)
    } else {
      store.unpinSticker(sticker.id)
    }
  }

  const onAdd = (sticker: Tag) => {
    store.pinSticker(sticker.id, '')
  }

  return (
    <>
      <TwAction>
        <Checkbox
          value={!!state.pin}
          text={lang.createPinStickers}
          onChange={store.pinChanged}
        />
      </TwAction>

      {state.pin && board?.id && (
        <TwStickerPanel>
          {selectedStickers.map(sticker => (
            <StickerAction
              key={sticker.id}
              sticker={sticker}
              boardId={board.id}
              onChange={onChange}
              value={values?.[sticker.id]}
            />
          ))}
        </TwStickerPanel>
      )}

      {state.pin && (
        <div onMouseDown={noPropagate} onClick={noPropagate}>
          <PinStickersDropdown
            stickers={stickersToSelect}
            onClickOnSticker={onAdd}
          >
            <div className="mt-8 ml-24">
              <LinkButtonAdd
                text={Lang.msg.chat.addSticker}
                type={'circle'}
                onMouseDown={() => {
                  return
                }}
              />
            </div>
          </PinStickersDropdown>
        </div>
      )}
    </>
  )
})

interface StickerActionProps {
  sticker: Tag
  boardId: DataObjectId
  onChange: (sticker: Tag, value: GenericStickerState | undefined) => void
  value: GenericStickerState
}

const StickerAction = (props: StickerActionProps) => {
  const {sticker, onChange} = props

  const onPin = (value: GenericStickerState) => {
    onChange(sticker, value)
  }

  const onUnpin = () => {
    onChange(sticker, undefined)
  }

  return (
    <StickerItem
      withMenu
      location="task"
      onPin={onPin}
      onUnpin={onUnpin}
      sticker={sticker}
      currentValue={props.value}
    />
  )
}
